/**
 * Période sélectionnée, stockée dans l'URL.
 *
 * Trois raisons de la mettre là plutôt que dans un état React :
 *   · elle survit à la navigation entre les onglets — changer de section ne
 *     doit pas réinitialiser la période qu'on vient de choisir ;
 *   · l'URL devient partageable : « regarde les 90 derniers jours » est un lien ;
 *   · le rechargement de la page conserve le contexte.
 *
 * Et c'est zéro bibliothèque de plus : React Router expose déjà la query string.
 *
 * ── La borne haute est rabattue sur le dernier jour de données ───────────────
 * Hier pour l'usage de l'app (sync nocturne), aujourd'hui pour le reste
 * (cf. `DataScope` dans `lib/dates.ts`, déduit de l'adresse de la page). Une
 * URL partagée la semaine dernière porte une borne haute désormais dépassée :
 * on la ramène dessus au lieu d'ajouter une journée vide au bout de la courbe.
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  PERIOD_PRESETS,
  clampToLatestDay,
  clampToSelectable,
  dataScopeForPath,
  latestDataDay,
  presetRange,
  presetsForScope,
  type DataScope,
} from '../lib/dates';
import type { SelectedPeriod } from '../types';
import { useParisDay } from './useParisDay';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// Mois en cours par défaut : du 1er du mois au dernier jour de données,
// comparé automatiquement au même intervalle du mois précédent (cf.
// `comparisonWindows` côté API). Les jours à venir sont rabattus par l'API.
const DEFAULT_PRESET_ID = 'mtd';

function defaultPeriod(scope: DataScope): SelectedPeriod {
  const preset = PERIOD_PRESETS.find((p) => p.id === DEFAULT_PRESET_ID) ?? PERIOD_PRESETS[0];
  // PERIOD_PRESETS n'est jamais vide, mais le typage ne le sait pas.
  return { ...(preset ? preset.range(scope) : presetRange(30, scope)), presetId: preset?.id ?? DEFAULT_PRESET_ID };
}

export function usePeriodParam(): [SelectedPeriod, (next: SelectedPeriod) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const scope = dataScopeForPath(pathname);
  const today = useParisDay();

  const period = useMemo<SelectedPeriod>(() => {
    // Un préréglage est relatif (« 7 jours », « Aujourd'hui ») : on recalcule
    // ses bornes au lieu de croire celles de l'URL, écrites le jour du clic.
    // Sans ça, la web app reprise le lendemain afficherait encore la veille.
    const presetId = searchParams.get('preset');
    const preset = presetsForScope(scope).find((p) => p.id === presetId);
    if (preset) {
      return { ...preset.range(scope), presetId: preset.id };
    }

    const from = searchParams.get('from');
    const to = searchParams.get('to');

    // Une URL bricolee a la main ne doit pas casser la page : on retombe sur
    // la periode par defaut plutot que d'envoyer des dates invalides a l'API.
    if (!from || !to || !ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) {
      return defaultPeriod(scope);
    }

    // Les données s'arrêtent au dernier jour de la portée (cf. `latestDataDay`).
    // Une URL partagée la semaine dernière, ou un signet, porte une borne haute
    // désormais dépassée qui ajouterait une journée vide au bout de la courbe.
    // La fin peut aller jusqu'au dernier jour du mois en cours (période
    // pilotée), le début pas au-delà du dernier jour de données : une période
    // qui ne contiendrait aucun jour de données n'a rien à afficher.
    const clampedTo = clampToSelectable(to);
    const clampedFrom = clampToLatestDay(from, scope);

    // `from` entièrement dans le futur : il ne reste rien à afficher, et
    // rabattre les deux bornes ferait passer une période d'un jour pour un
    // choix de l'utilisateur.
    if (clampedFrom > clampedTo || clampedFrom > latestDataDay(scope)) {
      return defaultPeriod(scope);
    }

    return { from: clampedFrom, to: clampedTo, presetId };
    // `today` ne sert qu'à recalculer au changement de jour.
  }, [searchParams, scope, today]);

  const setPeriod = useCallback(
    (next: SelectedPeriod) => {
      const params: Record<string, string> = { from: next.from, to: next.to };
      if (next.presetId) params.preset = next.presetId;
      // `replace` : chaque clic sur un preset ne doit pas empiler une entree
      // dans l'historique, sinon le bouton « precedent » remonte les periodes
      // une par une au lieu de quitter la page.
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  return [period, setPeriod];
}
